import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Drifting Hebrew letters and music notes (brief §8.5 — KEEP THIS).
 * The behaviour is unchanged; the fonts and colours are Alephonics now.
 * Letters are set in Frank Ruhl Libre and drift gold → tekhelet as they
 * rise: the letter becoming sound, happening quietly in the background.
 * Sparse, 6–12% opacity, 20–40s cycles. Atmosphere, not content.
 */
const DRIFTING_LETTERS = [
  "א", "ש", "ב", "מ", "ה", "י", "ר", "ת",
  "נ", "ע", "ל", "ק", "ד", "ו", "ז", "ח",
  "פ", "צ", "ס", "ג", "ט", "כ",
];

const GOLD = "#F2D589";
const TEKHELET = "#FCA07A";

export function DriftingLetters({ count = 16 }) {
  const still = useReducedMotion();
  const items = Array.from({ length: count }).map((_, i) => {
    const ch = DRIFTING_LETTERS[(i * 5) % DRIFTING_LETTERS.length];
    const top = (i * 67) % 100;
    const left = (i * 41 + 13) % 100;
    const dur = 20 + ((i * 7) % 20);
    const delay = (i * 0.9) % 12;
    const size = 46 + ((i * 17) % 120);
    const opacity = 0.06 + ((i * 13) % 7) / 100;
    return { ch, top, left, dur, delay, size, opacity, key: `${ch}-${i}` };
  });
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {items.map((it) => (
        <motion.span
          key={it.key}
          className="absolute select-none"
          style={{
            top: `${it.top}%`,
            left: `${it.left}%`,
            fontFamily: "'Frank Ruhl Libre', serif",
            fontWeight: 400,
            fontSize: it.size,
            color: GOLD,
            opacity: it.opacity,
            filter: "blur(0.2px)",
          }}
          animate={
            still
              ? undefined
              : {
                  y: [0, -46, 0, 30, 0],
                  x: [0, 18, -12, 12, 0],
                  color: [GOLD, GOLD, TEKHELET, TEKHELET, GOLD],
                }
          }
          transition={{
            duration: it.dur,
            delay: it.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        >
          {it.ch}
        </motion.span>
      ))}
    </div>
  );
}

// Music notes stay music notes — they just rise in tekhelet now.
const NOTE_GLYPHS = ["\u266A", "\u266B", "\u266C", "\u2669", "\u266D", "\u266F"];

export function FloatingNotes({ count = 6 }) {
  const still = useReducedMotion();
  const items = Array.from({ length: count }).map((_, i) => {
    const ch = NOTE_GLYPHS[i % NOTE_GLYPHS.length];
    const left = (i * 73 + 11) % 100;
    const dur = 26 + ((i * 5) % 14);
    const delay = (i * 1.6) % 12;
    const size = 20 + ((i * 11) % 30);
    const drift = ((i * 19) % 60) - 30;
    return { ch, left, dur, delay, size, drift, key: `note-${i}` };
  });
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {items.map((it) => (
        <motion.span
          key={it.key}
          className="absolute select-none"
          style={{
            left: `${it.left}%`,
            bottom: "-8%",
            fontSize: it.size,
            color: TEKHELET,
            opacity: still ? 0.1 : 0,
          }}
          initial={{ y: 0, opacity: 0 }}
          animate={
            still
              ? undefined
              : {
                  y: ["0vh", "-110vh"],
                  x: [0, it.drift, -it.drift / 2, it.drift / 3, 0],
                  opacity: [0, 0.12, 0.12, 0.08, 0],
                }
          }
          transition={{
            duration: it.dur,
            delay: it.delay,
            repeat: Infinity,
            ease: "linear",
          }}
        >
          {it.ch}
        </motion.span>
      ))}
    </div>
  );
}


// Tiny twinkling sparkles, barely-there fairy dust.
export function Sparkles({ count = 50 }) {
  const still = useReducedMotion();
  if (still) return null;
  const items = Array.from({ length: count }).map((_, i) => {
    const top = (i * 53 + 7) % 100;
    const left = (i * 37 + 19) % 100;
    const dur = 2.4 + ((i * 0.3) % 3.5);
    const delay = (i * 0.27) % 5;
    const size = 2 + ((i * 5) % 5);
    return { top, left, dur, delay, size, key: `sp-${i}` };
  });
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{ zIndex: 0 }}
    >
      {items.map((it) => (
        <motion.span
          key={it.key}
          className="absolute rounded-full"
          style={{
            top: `${it.top}%`,
            left: `${it.left}%`,
            width: it.size,
            height: it.size,
            background: "#FFF7DC",
            boxShadow: "0 0 12px rgba(255,247,220,0.9)",
          }}
          animate={{ opacity: [0, 0.9, 0], scale: [0.6, 1.2, 0.6] }}
          transition={{
            duration: it.dur,
            delay: it.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
}
